import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Visibility } from '@mui/icons-material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import FabricationDocumentRowActions from './FabricationDocumentRowActions'

// Historia 148 — UNA fila = UNA aparición de un documento bajo UN registro de procedencia.
//
// ⚠️ RN15/S54: un documento colgado de dos procedencias tiene DOS filas, una por grupo, y no se
// deduplica por uuid. Cada fila lleva SU procedencia (`sourceType` + `sourceReferenceId`) en los
// atributos de datos y se la pasa a las acciones: al actuar se envía al servidor la procedencia de
// ESA aparición (D4/RN35), nunca «la del documento».
//
// ⚠️ RN16/S55: las acciones ofrecidas son EXACTAMENTE las que el grupo trae en `permissions`. No
// hay constante en el cliente, no hay acción pintada en gris: la que no está concedida sencillamente
// no se pinta.
//
// ⚠️ RN31/S55 — REPARTO DE LA FILA. Ver es la acción principal y se queda VISIBLE; descargar,
// editar y eliminar se recogen en el kebab, con la misma gramática que las tablas del resto de MIC.
// No es solo estética: cuatro iconos de 48 px se comían el ancho y partían el nombre del documento
// en cinco líneas. El nombre manda, porque es lo que el operario lee para elegir.
//
// El nombre va en su propia línea y las etiquetas debajo: en un panel estrecho, nombre y chips en la
// misma fila dejan al nombre sin sitio.

const rowSx = (selected) => ({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: 1,
  cursor: 'pointer',
  // La fila SELECCIONADA es la que se está viendo en el panel derecho: el operario tiene que saber
  // de un vistazo qué documento está mirando.
  bgcolor: selected ? 'action.selected' : 'transparent',
  borderLeft: (theme) => '3px solid ' + (selected ? theme.palette.primary.main : 'transparent'),
  '&:hover': { bgcolor: 'action.hover' }
})

// Área táctil de taller (RN30/D18).
const VIEW_ACTION_SX = { minWidth: 48, minHeight: 48 }

/**
 * @param {Object} props
 * @param {string} props.sourceType Tipo de procedencia del GRUPO en el que se pinta esta aparición
 * @param {Object} props.entry Entrada del contrato N1: `sourceReferenceId`, `sourceCode`,
 * `sourceName` y el bloque `document` del DTO documental de MIC, verbatim
 * @param {Object} props.permissions Los 5 booleanos del grupo
 * @param {boolean} props.selected Si es la aparición que se está viendo en el panel derecho
 * @param {Function} props.onView @param {Function} props.onDownload
 * @param {Function} props.onEdit @param {Function} props.onDelete
 */
const FabricationDocumentEntryRow = ({
  sourceType,
  entry,
  permissions,
  selected = false,
  onView = () => {},
  onDownload = () => {},
  onEdit = () => {},
  onDelete = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const document = entry.document
  const documentTypeName = document?.documentType?.name || ''
  // La procedencia de ESTA aparición, que es lo que viaja al servidor en cualquier acción.
  const source = {
    sourceType,
    sourceReferenceId: entry.sourceReferenceId,
    sourceCode: entry.sourceCode
  }

  // Toda la fila abre el documento en el panel derecho: con guantes, acertar en la fila entera es
  // más fácil que acertar en un icono.
  return (
    <Box
      component='li'
      data-testid='document-entry'
      data-source-type={sourceType}
      data-source-reference-id={String(entry.sourceReferenceId)}
      data-document-uuid={document?.uuid || ''}
      data-selected={selected ? 'true' : 'false'}
      onClick={permissions.view ? () => onView(entry, source) : undefined}
      sx={rowSx(selected)}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant='body1' sx={{ fontWeight: 700, wordBreak: 'break-word', lineHeight: 1.3 }}>
          {document?.documentName || ''}
        </Typography>
        <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {/* RN11: de qué REGISTRO cuelga. Sin él, en un grupo con tres materias primas el operario
              no sabe cuál es el certificado que busca. */}
          <Chip size='small' variant='outlined' label={entry.sourceCode} />
          {/* Un documento sin tipo no imprime «undefined»: el chip sencillamente no se pinta. */}
          {documentTypeName !== '' ? <Chip size='small' color='info' variant='outlined' label={documentTypeName} /> : null}
          {document?.fileName
            ? (
              <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                {document.fileName}
              </Typography>
              )
            : null}
        </Stack>
      </Stack>

      <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flexShrink: 0 }}>
        {permissions.view
          ? (
            <Tooltip title={t_('View file')}>
              <IconButton
                aria-label={t_('View file')}
                data-primary-action='true'
                color='primary'
                size='large'
                sx={VIEW_ACTION_SX}
                onClick={(event) => { event.stopPropagation(); onView(entry, source) }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            )
          : null}
        <FabricationDocumentRowActions
          entry={entry}
          source={source}
          permissions={permissions}
          onDownload={onDownload}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Stack>
    </Box>
  )
}

FabricationDocumentEntryRow.propTypes = {
  sourceType: PropTypes.string.isRequired,
  entry: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onView: PropTypes.func,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
}

export default FabricationDocumentEntryRow
